import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Button,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';

interface IDevice {
  customerNo: string;
  os: string;
  userSerial: string;
  token: string;
  dateCreated: string;
  lastUsed: string;
}

const DevicesList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [devices, setDevices] = useState<IDevice[]>([]);
  const [loading, setLoading] = useState(false);
  const [customerNo, setCustomerNo] = useState('');
  const [userSerial, setUserSerial] = useState('');
  const [error, setError] = useState('');

  const loadDevices = async () => {
    if (!customerNo && !userSerial) {
      setError('Customer No or User Serial are required');
      return;
    }

    setError('');
    setLoading(true);
    try {
      const params: any = {};
      if (customerNo) params.customerNo = customerNo;
      if (userSerial) params.userSerial = userSerial;

      const response = await axios.get(`${environment.apiUrl}token`, { params });
      console.log('Devices response:', response.data);

      const devicesData = Array.isArray(response.data) ? response.data : [];
      setDevices(devicesData);
    } catch (error) {
      console.error('Error loading devices:', error);
      setDevices([]);
      setError('Network error. Try again later');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadDevices();
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }} gutterBottom>
        Registered Devices
      </Typography>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
            <TextField
              label="Customer No"
              value={customerNo}
              onChange={(e) => setCustomerNo(e.target.value)}
              size="small"
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
            />
            <TextField
              label="User Serial"
              value={userSerial}
              onChange={(e) => setUserSerial(e.target.value)}
              size="small"
              sx={{ flex: '1 1 200px', minWidth: '150px' }}
            />
            <Button 
              variant="contained" 
              onClick={handleSearch}
              sx={{ minWidth: '120px', height: '40px' }}
            >
              Search
            </Button>
          </Box>
          {error && (
            <Alert severity="warning">
              {error}
            </Alert>
          )}
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : devices.length > 0 ? (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Customer No</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>OS</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' }, minWidth: 100 }}>User Serial</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' }, minWidth: 200 }}>Token</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 150 }}>Date Created</TableCell>
                  <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 150 }}>Last Used</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {devices.map((device, index) => (
                  <TableRow 
                    key={index}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ wordBreak: 'break-word' }}>{device.customerNo}</TableCell>
                    <TableCell>{device.os}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, wordBreak: 'break-word' }}>{device.userSerial}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', display: { xs: 'none', md: 'table-cell' }, wordBreak: 'break-all' }}>
                      {device.token}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {new Date(device.dateCreated).toLocaleString()}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      {new Date(device.lastUsed).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        !loading && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="body1" color="text.secondary" align="center">
              No devices found. Please enter search criteria and click Search.
            </Typography>
          </Paper>
        )
      )}
    </Box>
  );
};

export default DevicesList;
